import {keys} from 'lodash';
import {useMemo} from 'react';

import {useOptionalPart, useParts} from '../hooks';
import {type WholeFromTypeWithRef} from '../types';
import type * as PanelsTypes from './types';

export function useOptionalPanels(
  ref?: PanelsTypes.Ref
): WholeFromTypeWithRef<'panels'> | undefined {
  const panels = useOptionalPart(ref);
  const key = useMemo(() => {
    if (panels == null) {
      return undefined;
    }
    const k = keys(panels.views)[0];
    if (k == null) {
      // item.views used to be used to store different sets of panels for tabs.
      // This is no longer the case, and the panels structure always contains a
      // single key.
      throw new Error('invalid state: panels.views missing key');
    }
    return k;
  }, [panels]);

  const panelConfigRefs = useMemo(() => {
    if (panels == null || key == null) {
      return [];
    }
    return panels.views[key].panelRefs;
  }, [panels, key]);

  const panelConfigs = useParts(panelConfigRefs);

  return useMemo(() => {
    if (panels && key && panelConfigs && ref) {
      return {
        ...panels,
        ref,
        views: {
          [key]: {
            ...panels.views[key],
            config: panelConfigs,
          },
        },
      };
    }
    return undefined;
  }, [panels, ref, key, panelConfigs]);
}
